/**
 * Recipe detail — mockup-aligned V2 layout.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaStar } from 'react-icons/fa';
import { communityRecipesService } from '../services/communityRecipesService';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import {
  RECIPE_REPORT_CATEGORIES,
  MAX_RECIPE_PHOTOS,
  ingredientUnitLabel,
} from '../constants/communityRecipes';
import { proteinLabel } from '../campRecipesFilters';
import Lightbox from '../../../components/common/Lightbox';
import { RecipeStarsInput } from './RecipeStarsInput';
import { CommunityRecipesPageShell } from './CommunityRecipesPageShell';
import { YButton } from '../../../ui/YButton';
import './camp-essentials-shell.css';
import './camp-recipes-shell.css';

export function CommunityRecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { currentUser } = useCurrentUser();
  const currentUserId = currentUser?.id;
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCategory, setReportCategory] = useState('inappropriate_photo');
  const [reportMessage, setReportMessage] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [ratingBusy, setRatingBusy] = useState(false);

  const load = () => communityRecipesService.get(id).then(setRecipe);

  useEffect(() => {
    let cancelled = false;
    communityRecipesService
      .get(id)
      .then((data) => {
        if (!cancelled) setRecipe(data);
      })
      .catch(() => {
        if (!cancelled) setError('Recipe not found or unavailable.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isAuthor =
    recipe &&
    recipe.source === 'community' &&
    currentUserId != null &&
    Number(recipe.author_id) === Number(currentUserId);

  const handleFavorite = async () => {
    if (!recipe) return;
    try {
      const next = !recipe.is_favorited;
      await communityRecipesService.setFavorite(recipe.id, next);
      setRecipe((prev) => (prev ? { ...prev, is_favorited: next } : prev));
      setActionMsg(next ? 'Saved to favorites.' : 'Removed from favorites.');
    } catch {
      setActionMsg('Could not update favorite.');
    }
  };

  const handleStars = async (stars) => {
    if (!recipe || ratingBusy) return;
    setRatingBusy(true);
    try {
      const updated = await communityRecipesService.setRating(recipe.id, stars);
      setRecipe(updated);
      setActionMsg('Thanks for your rating.');
    } catch {
      setActionMsg('Could not save rating.');
    } finally {
      setRatingBusy(false);
    }
  };

  const handleReport = async (e) => {
    e.preventDefault();
    if (!recipe) return;
    try {
      await communityRecipesService.report(recipe.id, reportCategory, reportMessage);
      setReportOpen(false);
      setActionMsg('Thanks — we’ll review your report.');
    } catch {
      setActionMsg('Could not submit report.');
    }
  };

  const handleDelete = async () => {
    if (!recipe || !window.confirm('Delete this recipe permanently? This cannot be undone.')) return;
    try {
      await communityRecipesService.delete(recipe.id);
      navigate('/camp-essentials/recipes');
    } catch {
      setActionMsg('Could not delete recipe.');
    }
  };

  const handlePhoto = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const img = files.find((f) => f.type.startsWith('image/'));
    if (!img || !recipe) return;
    if ((recipe.photos || []).length >= MAX_RECIPE_PHOTOS) return;
    setUploading(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
    try {
      await communityRecipesService.uploadRecipePhoto(recipe.id, img);
      await load();
    } catch {
      setError('Photo upload failed.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <CommunityRecipesPageShell
        title="Recipe"
        testId="camp-recipe-detail"
      >
        <p className="camp-recipes-page__loading">Loading…</p>
      </CommunityRecipesPageShell>
    );
  }

  if (error || !recipe) {
    return (
      <CommunityRecipesPageShell
        title="Recipe"
        testId="camp-recipe-detail"
      >
        <p className="camp-recipes-page__error">{error || 'Not found.'}</p>
      </CommunityRecipesPageShell>
    );
  }

  const photo = (recipe.photos || [])[0];
  const photoUrl = photo?.photo_url || photo?.thumb_url || recipe.thumbnail_url;
  const protein =
    recipe.main_protein_display || proteinLabel(recipe.main_protein) || null;
  const ratingCount = recipe.rating_count ?? 0;

  return (
    <CommunityRecipesPageShell
      title={recipe.title}
      subtitle={
        recipe.source === 'curated'
          ? 'Curated recipe'
          : 'Community recipe'
      }
      testId="camp-recipe-detail"
      panelClassName="camp-recipes-page__panel--detail"
    >
      <article className="camp-recipes-detail-v2">
        {photoUrl ? (
          <button
            type="button"
            className="camp-recipes-detail-v2__hero"
            onClick={() => setLightboxOpen(true)}
            aria-label="View recipe photo"
          >
            <img src={photoUrl} alt="" />
            <div className="camp-recipes-detail-v2__hero-overlay" aria-hidden="true" />
            <div className="camp-recipes-detail-v2__hero-content">
              <div className="camp-recipes-detail-v2__chips">
                {protein ? (
                  <span className="camp-recipes-detail-v2__chip">{protein}</span>
                ) : null}
                <span className="camp-recipes-detail-v2__chip">
                  {recipe.source === 'curated' ? 'Curated' : 'Community'}
                </span>
                {recipe.avg_stars != null ? (
                  <span className="camp-recipes-detail-v2__chip">
                    <FaStar aria-hidden="true" /> {recipe.avg_stars}
                  </span>
                ) : null}
              </div>
              <h2 className="camp-recipes-detail-v2__hero-title">{recipe.title}</h2>
              {recipe.description ? (
                <p className="camp-recipes-detail-v2__hero-desc">{recipe.description}</p>
              ) : null}
            </div>
          </button>
        ) : (
          <div className="camp-recipes-detail-v2__chips camp-recipes-detail-v2__chips--inline">
            {protein ? (
              <span className="camp-recipes-detail-v2__chip camp-recipes-detail-v2__chip--inline">
                {protein}
              </span>
            ) : null}
            <span className="camp-recipes-detail-v2__chip camp-recipes-detail-v2__chip--inline">
              {recipe.source === 'curated' ? 'Curated' : 'Community'}
            </span>
          </div>
        )}

        <div className="camp-recipes-detail-v2__stats" aria-label="Recipe stats">
          <div className="camp-recipes-detail-v2__stat">
            <p className="camp-recipes-detail-v2__stat-value">
              {recipe.avg_stars != null ? recipe.avg_stars : '—'}
            </p>
            <p className="camp-recipes-detail-v2__stat-label">Rating</p>
          </div>
          <div className="camp-recipes-detail-v2__stat">
            <p className="camp-recipes-detail-v2__stat-value">{ratingCount}</p>
            <p className="camp-recipes-detail-v2__stat-label">Reviews</p>
          </div>
          <div className="camp-recipes-detail-v2__stat">
            <p className="camp-recipes-detail-v2__stat-value">
              {(recipe.ingredients || []).length}
            </p>
            <p className="camp-recipes-detail-v2__stat-label">Ingredients</p>
          </div>
        </div>

        {actionMsg ? <p className="camp-recipes-page__message">{actionMsg}</p> : null}

        {isAuthor && (recipe.photos || []).length < MAX_RECIPE_PHOTOS ? (
          <div className="mb-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="d-none"
              disabled={uploading}
              onChange={handlePhoto}
              aria-label="Add recipe photo"
            />
            <YButton
              type="button"
              variant="outline-secondary"
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Uploading…' : 'Add photo'}
            </YButton>
          </div>
        ) : null}

        {!photoUrl && recipe.description ? (
          <p className="camp-recipes-detail-v2__instructions mb-4">{recipe.description}</p>
        ) : null}

        <h3 className="camp-recipes-detail-v2__section-title">Ingredients</h3>
        <ul className="camp-recipes-detail-v2__ingredients">
          {(recipe.ingredients || []).map((row, i) => (
            <li key={i}>
              {row.name}
              {row.quantity || row.unit
                ? ` — ${[row.quantity, ingredientUnitLabel(row.unit)].filter(Boolean).join(' ')}`
                : ''}
              {row.note ? ` (${row.note})` : ''}
            </li>
          ))}
        </ul>

        <h3 className="camp-recipes-detail-v2__section-title">Instructions</h3>
        <div className="camp-recipes-detail-v2__instructions">{recipe.instructions}</div>

        <div className="camp-recipes-detail-v2__actions">
          <YButton type="button" variant="outline-primary" onClick={handleFavorite}>
            {recipe.is_favorited ? 'Remove favorite' : 'Favorite'}
          </YButton>
          <span className="small text-muted">Your stars:</span>
          <RecipeStarsInput
            value={recipe.my_stars}
            onRate={handleStars}
            disabled={ratingBusy}
          />
          {isAuthor ? (
            <>
              <YButton
                type="button"
                variant="outline-secondary"
                onClick={() => navigate(`/camp-essentials/recipes/${recipe.id}/edit`)}
              >
                Edit
              </YButton>
              <YButton type="button" variant="outline-danger" onClick={handleDelete}>
                Delete
              </YButton>
            </>
          ) : (
            <YButton type="button" variant="outline-danger" onClick={() => setReportOpen(true)}>
              Report
            </YButton>
          )}
        </div>

        {reportOpen ? (
          <form className="camp-recipes-page__panel camp-recipes-page__panel--form mt-4" onSubmit={handleReport}>
            <div className="fw-semibold mb-2">Report inappropriate content</div>
            <select
              className="form-select form-select-sm mb-2"
              value={reportCategory}
              onChange={(e) => setReportCategory(e.target.value)}
            >
              {RECIPE_REPORT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <textarea
              className="form-control form-control-sm mb-2"
              rows={2}
              placeholder="Optional details"
              value={reportMessage}
              onChange={(e) => setReportMessage(e.target.value)}
            />
            <div className="d-flex gap-2">
              <YButton type="submit" variant="danger" size="sm">
                Submit report
              </YButton>
              <YButton type="button" variant="light" size="sm" onClick={() => setReportOpen(false)}>
                Cancel
              </YButton>
            </div>
          </form>
        ) : null}

        {lightboxOpen && photoUrl ? (
          <Lightbox
            items={[
              {
                caption: recipe.title,
                url: photo?.photo_url || photo?.thumb_url || recipe.thumbnail_url || '',
                getUrl: () => photo?.photo_url || photo?.thumb_url || recipe.thumbnail_url || '',
              },
            ]}
            initialIndex={0}
            onClose={() => setLightboxOpen(false)}
          />
        ) : null}
      </article>
    </CommunityRecipesPageShell>
  );
}
